// Dashboard component - Refactored into smaller components
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { dashboardAPI } from '../utils/api';
import serviceDataService from '../services/serviceDataService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import PageToolbar from '../components/PageToolbar';
import DashboardAdminOverview from '../components/dashboard/DashboardAdminOverview';
import DashboardModals from '../components/dashboard/DashboardModals';
import useDashboardData from '../hooks/useDashboardData';
import useDashboardFilters from '../hooks/useDashboardFilters';

const Dashboard = () => {
    const { userRole } = useAuth();
    const { showSuccess, showError } = useNotification();

    // Use custom hooks for data and filters
    const {
        stats,
        dashboardData,
        serviceData,
        loading,
        fetchDashboardData,
        fetchAdminDashboardData
    } = useDashboardData(userRole, showError);

    const {
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        handlePreviousPeriod,
        handleNextPeriod,
        getDateRangeText
    } = useDashboardFilters(userRole, fetchAdminDashboardData);

    const dateFilter = { startDate, endDate };

    // Modal state'leri
    const [showActiveCustomersModal, setShowActiveCustomersModal] = useState(false);
    const [showExpenseListModal, setShowExpenseListModal] = useState(false);
    const [showCashIncomeModal, setShowCashIncomeModal] = useState(false);
    const [showCashFlowModal, setShowCashFlowModal] = useState(false);
    const [showProfitModal, setShowProfitModal] = useState(false);
    const [showServiceDataModal, setShowServiceDataModal] = useState(false);
    const [showSoldQuantityModal, setShowSoldQuantityModal] = useState(false);
    const [soldProducts, setSoldProducts] = useState([]);
    const [loadingSoldProducts, setLoadingSoldProducts] = useState(false);
    const [showCarSoldQuantityModal, setShowCarSoldQuantityModal] = useState(false);
    const [carSoldProducts, setCarSoldProducts] = useState([]);
    const [loadingCarSoldProducts, setLoadingCarSoldProducts] = useState(false);

    // Service form state
    const [serviceFormDraft, setServiceFormDraft] = useState({ income: [], expenses: [] });
    const [serviceFormRemoved, setServiceFormRemoved] = useState({ income: [], expenses: [] });
    const [serviceFormDirty, setServiceFormDirty] = useState(false);
    const [savingServiceForm, setSavingServiceForm] = useState(false);

    // Cash expense state
    const [cashExpenseDraft, setCashExpenseDraft] = useState([]);
    const [cashExpenseRemoved, setCashExpenseRemoved] = useState([]);
    const [cashExpenseDirty, setCashExpenseDirty] = useState(false);
    const [savingCashExpenses, setSavingCashExpenses] = useState(false);
    const [cashExpenseModalLoading, setCashExpenseModalLoading] = useState(false);
    const [cashIncomeModalLoading, setCashIncomeModalLoading] = useState(false);
    const [cashIncomeData, setCashIncomeData] = useState([]);

    // Cash income state
    const [cashIncomeDraft, setCashIncomeDraft] = useState([]);
    const [cashIncomeRemoved, setCashIncomeRemoved] = useState([]);
    const [cashIncomeDirty, setCashIncomeDirty] = useState(false);
    const [savingCashIncome, setSavingCashIncome] = useState(false);

    // Helper functions
    const getTotalAmount = (items) => {
        return items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    };

    // Effect to initialize data
    useEffect(() => {
        const loadData = async () => {
            await fetchDashboardData(startDate, endDate);
            // Fetch cash income data
            try {
                const dateStr = startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0');
                const response = await dashboardAPI.getCashIncome({ date: dateStr });
                setCashIncomeData(response.data || []);
            } catch (error) {
                console.error('Cash income fetch error:', error);
                setCashIncomeData([]);
            }
        };
        loadData();
    }, [fetchDashboardData, startDate, endDate]);

    // Effect to fetch service data when modal opens
    useEffect(() => {
        if (showServiceDataModal && userRole === 'admin') {
            fetchAdminDashboardData(startDate, endDate);
        }
    }, [showServiceDataModal, userRole, startDate, endDate, fetchAdminDashboardData]);

    // Effect to sync service form draft when serviceData changes
    // Only sync if the form is NOT dirty (protects user edits)
    useEffect(() => {
        if (showServiceDataModal && serviceData && !serviceFormDirty) {
            setServiceFormDraft({
                income: (serviceData.income || []).map(item => ({ ...item, isNew: false })),
                expenses: (serviceData.expenses || []).map(item => ({ ...item, isNew: false }))
            });
            setServiceFormRemoved({ income: [], expenses: [] });
            setServiceFormDirty(false);
        }
    }, [showServiceDataModal, serviceData, serviceFormDirty]);

    // Effect to initialize cash expense draft
    useEffect(() => {
        if (showExpenseListModal && dashboardData.expenseData) {
            setCashExpenseDraft(
                (dashboardData.expenseData || []).map(expense => ({
                    ...expense,
                    amount: expense.amount,
                    isNew: false
                }))
            );
            setCashExpenseRemoved([]);
            setCashExpenseDirty(false);
        }
    }, [showExpenseListModal, dashboardData.expenseData]);

    // Effect to initialize cash income draft
    useEffect(() => {
        if (showCashIncomeModal) {
            // Load existing cash income from daily_revenue_manual
            const loadCashIncome = async () => {
                setCashIncomeModalLoading(true);
                try {
                    const dateStr = startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0');
                    const response = await dashboardAPI.getCashIncome({ date: dateStr });
                    setCashIncomeDraft((response.data || []).map(item => ({
                        ...item,
                        isNew: false
                    })));
                    setCashIncomeRemoved([]);
                    setCashIncomeDirty(false);
                } catch (error) {
                    console.error('Cash income load error:', error);
                } finally {
                    setCashIncomeModalLoading(false);
                }
            };
            loadCashIncome();
        }
    }, [showCashIncomeModal, startDate]);

    // Handler functions for modals
    const handleSoldQuantityClick = async () => {
        setShowSoldQuantityModal(true);
        await fetchSoldProducts();
    };

    const fetchCarSoldProducts = async () => {
        setLoadingCarSoldProducts(true);
        try {
            const response = await dashboardAPI.getCarSoldProductsQuantity({
                startDate: startDate,
                endDate: endDate
            });
            setCarSoldProducts(response.data || []);
        } catch (error) {
            console.error('Car sold products fetch error:', error);
            setCarSoldProducts([]);
        } finally {
            setLoadingCarSoldProducts(false);
        }
    };

    const handleCarSoldQuantityClick = async () => {
        setShowCarSoldQuantityModal(true);
        await fetchCarSoldProducts();
    };

    const fetchSoldProducts = async () => {
        setLoadingSoldProducts(true);
        try {
            const response = await dashboardAPI.getSoldProductsQuantity({
                startDate: startDate,
                endDate: endDate
            });
            setSoldProducts(response.data || []);
        } catch (error) {
            console.error('Sold products fetch error:', error);
            setSoldProducts([]);
        } finally {
            setLoadingSoldProducts(false);
        }
    };

    // Service form handlers
    const addServiceDraftItem = (type) => {
        const newId = Date.now();
        setServiceFormDraft(prev => ({
            ...prev,
            [type]: [...(prev[type] || []), { id: newId, name: '', amount: 0, isNew: true }]
        }));
        setServiceFormDirty(true);
    };

    const removeServiceDraftItem = (type, id) => {
        setServiceFormDraft(prev => ({
            ...prev,
            [type]: prev[type].filter(item => item.id !== id)
        }));
        setServiceFormRemoved(prev => ({
            ...prev,
            [type]: [...prev[type], id]
        }));
        setServiceFormDirty(true);
    };

    const handleServiceDraftChange = (type, id, field, value) => {
        setServiceFormDraft(prev => ({
            ...prev,
            [type]: prev[type].map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        }));
        setServiceFormDirty(true);
    };

    const handleSaveServiceData = async () => {
        setSavingServiceForm(true);
        try {
            // Combine removed items from both income and expenses
            const allRemovedItems = [
                ...serviceFormRemoved.income,
                ...serviceFormRemoved.expenses
            ];

            // Validate data before saving
            const validIncomeItems = serviceFormDraft.income.filter(item => item.name && parseFloat(item.amount || 0) > 0);
            const validExpenseItems = serviceFormDraft.expenses.filter(item => item.name && parseFloat(item.amount || 0) > 0);

            // If no valid items to save and nothing to remove, show warning
            if (validIncomeItems.length === 0 && validExpenseItems.length === 0 && allRemovedItems.length === 0) {
                showError('Kaydedecek herhangi bir veri yok. Lütfen gelir veya gider ekleyin.');
                setSavingServiceForm(false);
                return;
            }

            const result = await serviceDataService.saveServiceData({
                income: validIncomeItems,
                expenses: validExpenseItems,
                removed: allRemovedItems,
                date: startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0')
            });

            if (result.success) {
                showSuccess(result.message || 'Servis verileri kaydedildi');
                setShowServiceDataModal(false);
                setServiceFormDirty(false);
                // Refresh data
                await fetchAdminDashboardData(startDate, endDate);
            } else {
                showError('Servis verileri kaydedilemedi: ' + (result.error || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Service data save error:', error);
            showError('Servis verileri kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'));
        } finally {
            setSavingServiceForm(false);
        }
    };

    // Cash expense handlers
    const addCashExpenseDraft = () => {
        const newTempId = Date.now();
        setCashExpenseDraft(prev => [...(prev || []), {
            tempId: newTempId,
            description: '',
            amount: 0,
            isNew: true
        }]);
        setCashExpenseDirty(true);
    };

    const removeCashExpenseDraft = (tempId) => {
        setCashExpenseDraft(prev => prev.filter(expense => expense.tempId !== tempId && expense.id !== tempId));
        // If it's a real ID (not temp), add to removed list
        const itemToRemove = cashExpenseDraft.find(expense => expense.tempId === tempId || expense.id === tempId);
        if (itemToRemove && itemToRemove.id && !itemToRemove.isNew) {
            setCashExpenseRemoved(prev => [...prev, itemToRemove.id]);
        }
        setCashExpenseDirty(true);
    };

    const handleCashExpenseDraftChange = (id, field, value) => {
        setCashExpenseDraft(prev => prev.map(expense =>
            expense.id === id || expense.tempId === id ? { ...expense, [field]: value } : expense
        ));
        setCashExpenseDirty(true);
    };

    const handleSaveCashExpenses = async () => {
        setSavingCashExpenses(true);
        try {
            // Validate data before saving
            const validExpenses = cashExpenseDraft.filter(expense => expense.description && expense.amount > 0);

            // If no valid items to save and nothing to remove, show warning
            if (validExpenses.length === 0 && cashExpenseRemoved.length === 0) {
                showError('Kaydedecek herhangi bir veri yok. Lütfen gider ekleyin.');
                setSavingCashExpenses(false);
                return;
            }

            const result = await dashboardAPI.saveCashExpenses({
                expenses: validExpenses,
                removed: cashExpenseRemoved,
                date: startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0')
            });

            // Update cashExpenseDraft with real UUIDs from database
            if (result.data && result.data.insertedIds && result.data.insertedIds.length > 0) {
                setCashExpenseDraft(prev => prev.map(item => {
                    const insertedItem = result.data.insertedIds.find(id => id.tempId === item.tempId);
                    if (insertedItem) {
                        return { ...item, id: insertedItem.realId, tempId: null, isNew: false };
                    }
                    return item;
                }));
            }

            showSuccess('Kasa giderleri kaydedildi');
            setShowExpenseListModal(false);
            setCashExpenseDirty(false);
            // Refresh data
            await fetchAdminDashboardData(startDate, endDate);
            // Refresh cash income data
            try {
                const dateStr = startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0');
                const response = await dashboardAPI.getCashIncome({ date: dateStr });
                setCashIncomeData(response.data || []);
            } catch (error) {
                console.error('Cash income fetch error:', error);
                setCashIncomeData([]);
            }
        } catch (error) {
            console.error('Cash expenses save error:', error);
            showError('Kasa giderleri kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'));
        } finally {
            setSavingCashExpenses(false);
        }
    };

    // Cash income handlers
    const addCashIncomeDraft = () => {
        const newTempId = Date.now();
        setCashIncomeDraft(prev => [...(prev || []), {
            tempId: newTempId,
            description: '',
            amount: 0,
            isNew: true
        }]);
        setCashIncomeDirty(true);
    };

    const removeCashIncomeDraft = (tempId) => {
        setCashIncomeDraft(prev => prev.filter(income => income.tempId !== tempId && income.id !== tempId));
        // If it's a real ID (not temp), add to removed list
        const itemToRemove = cashIncomeDraft.find(income => income.tempId === tempId || income.id === tempId);
        if (itemToRemove && itemToRemove.id && !itemToRemove.isNew) {
            setCashIncomeRemoved(prev => [...prev, itemToRemove.id]);
        }
        setCashIncomeDirty(true);
    };

    const handleCashIncomeDraftChange = (id, field, value) => {
        setCashIncomeDraft(prev => prev.map(income =>
            income.id === id || income.tempId === id ? { ...income, [field]: value } : income
        ));
        setCashIncomeDirty(true);
    };

    const handleSaveCashIncome = async () => {
        setSavingCashIncome(true);
        try {
            // Validate data before saving
            const validIncome = cashIncomeDraft.filter(income => income.description && income.amount > 0);

            // If no valid items to save and nothing to remove, show warning
            if (validIncome.length === 0 && cashIncomeRemoved.length === 0) {
                showError('Kaydedecek herhangi bir veri yok. Lütfen gelir ekleyin.');
                setSavingCashIncome(false);
                return;
            }

            const result = await dashboardAPI.saveCashIncome({
                income: validIncome,
                removed: cashIncomeRemoved,
                date: startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0')
            });

            // Update cashIncomeDraft with real UUIDs from database
            if (result.data && result.data.insertedIds && result.data.insertedIds.length > 0) {
                setCashIncomeDraft(prev => prev.map(item => {
                    const insertedItem = result.data.insertedIds.find(id => id.tempId === item.tempId);
                    if (insertedItem) {
                        return { ...item, id: insertedItem.realId, tempId: null, isNew: false };
                    }
                    return item;
                }));
            }

            showSuccess('Kasa gelirleri kaydedildi');
            setShowCashIncomeModal(false);
            setCashIncomeDirty(false);
            // Refresh data
            await fetchAdminDashboardData(startDate, endDate);
            // Refresh cash income data
            try {
                const dateStr = startDate.getFullYear() + '-' + String(startDate.getMonth() + 1).padStart(2, '0') + '-' + String(startDate.getDate()).padStart(2, '0');
                const response = await dashboardAPI.getCashIncome({ date: dateStr });
                setCashIncomeData(response.data || []);
            } catch (error) {
                console.error('Cash income fetch error:', error);
                setCashIncomeData([]);
            }
        } catch (error) {
            console.error('Cash income save error:', error);
            showError('Kasa gelirleri kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'));
        } finally {
            setSavingCashIncome(false);
        }
    };

    // Main render logic
    const getDashboardContent = () => {
        switch (userRole) {
            case 'admin':
                return (
                    <div className="space-y-4">
                        {/* Ana Bölümler - Yan Yana */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <DashboardAdminOverview
                                dashboardData={dashboardData}
                                stats={stats}
                                serviceData={serviceData}
                                dateFilter={dateFilter}
                                getTotalAmount={getTotalAmount}
                                onServiceDataModalOpen={() => setShowServiceDataModal(true)}
                                onExpenseListModalOpen={() => setShowExpenseListModal(true)}
                                setCashExpenseModalLoading={setCashExpenseModalLoading}
                                setShowActiveCustomersModal={setShowActiveCustomersModal}
                                setShowProfitModal={setShowProfitModal}
                                onSoldQuantityModalOpen={handleSoldQuantityClick}
                                onCarSoldQuantityModalOpen={handleCarSoldQuantityClick}
                                section="sales"
                            />
                            <DashboardAdminOverview
                                dashboardData={dashboardData}
                                stats={stats}
                                serviceData={serviceData}
                                dateFilter={dateFilter}
                                getTotalAmount={getTotalAmount}
                                onServiceDataModalOpen={() => setShowServiceDataModal(true)}
                                onExpenseListModalOpen={() => setShowExpenseListModal(true)}
                                setCashExpenseModalLoading={setCashExpenseModalLoading}
                                setShowActiveCustomersModal={setShowActiveCustomersModal}
                                setShowProfitModal={setShowProfitModal}
                                section="customers"
                            />
                        </div>

                        {/* Nakit Akış Yönetimi - Tam Genişlik */}
                        <DashboardAdminOverview
                            dashboardData={dashboardData}
                            stats={stats}
                            serviceData={serviceData}
                            dateFilter={dateFilter}
                            getTotalAmount={getTotalAmount}
                            onServiceDataModalOpen={() => setShowServiceDataModal(true)}
                            onExpenseListModalOpen={() => setShowExpenseListModal(true)}
                            onCashIncomeModalOpen={() => setShowCashIncomeModal(true)}
                            setCashExpenseModalLoading={setCashExpenseModalLoading}
                            setShowActiveCustomersModal={setShowActiveCustomersModal}
                            setShowProfitModal={setShowProfitModal}
                            cashIncomeData={cashIncomeData}
                            section="cashflow"
                        />

                        {/* Rapor - Tam Genişlik */}
                        <DashboardAdminOverview
                            dashboardData={dashboardData}
                            stats={stats}
                            serviceData={serviceData}
                            dateFilter={dateFilter}
                            getTotalAmount={getTotalAmount}
                            onServiceDataModalOpen={() => setShowServiceDataModal(true)}
                            onExpenseListModalOpen={() => setShowExpenseListModal(true)}
                            setCashExpenseModalLoading={setCashExpenseModalLoading}
                            setShowActiveCustomersModal={setShowActiveCustomersModal}
                            setShowProfitModal={setShowProfitModal}
                            section="financial-summary"
                        />
                    </div>
                );

            case 'reception':
                return (
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">MÜŞTERİ YÖNETİMİ</h2>
                                <p className="text-sm text-gray-600 font-medium">Müşteri bilgileri ve istatistikler</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">TOPLAM MÜŞTERİ</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.customers.total}</p>
                                        <p className="text-xs text-gray-600 font-medium">Kayıtlı müşteri sayısı</p>
                                    </div>
                                </div>

                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">BU AY YENİ</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.customers.newThisPeriod}</p>
                                        <p className="text-xs text-gray-600 font-medium">Yeni kayıt sayısı</p>
                                    </div>
                                </div>

                                <div className="group relative overflow-hidden cursor-pointer" onClick={() => setShowActiveCustomersModal(true)}>
                                    <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:border-primary-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">AKTİF MÜŞTERİ</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.customers.active}</p>
                                        <p className="text-xs text-gray-600 font-medium">Aktif müşteri sayısı</p>
                                        <p className="text-xs text-primary-600 mt-1 font-medium">MÜŞTERİ ÖDEMELERİNİ GÖRÜNTÜLE</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'warehouse':
                return (
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">ÜRÜN YÖNETİMİ</h2>
                                <p className="text-sm text-gray-600 font-medium">Stok durumu ve ürün istatistikleri</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">TOPLAM ÜRÜN</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.products.total}</p>
                                    </div>
                                </div>

                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-amber-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-amber-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-amber-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">DÜŞÜK STOK</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.products.lowStock}</p>
                                    </div>
                                </div>

                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-red-50 rounded-xl"></div>
                                    <div className="relative p-5 bg-white rounded-xl border border-red-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-red-300">
                                        <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">STOKTA YOK</p>
                                        <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">{stats.products.outOfStock}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return <div>Geçersiz rol</div>;
        }
    };

    // Update logical loading check: Only show full-screen loader if we have NO data at all
    // If we have dashboardData, we can show the actual dashboard and let background updates happen
    if (loading && (!dashboardData || !dashboardData.dailyCashFlow)) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200 text-center max-w-md w-full">
                    <div className="flex justify-center mb-6">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Yükleniyor
                    </h3>
                    <p className="text-gray-600 text-sm">
                        Veriler getiriliyor...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900">
            <PageToolbar
                subtitle={getDateRangeText()}
                right={
                    <button
                        onClick={() => fetchDashboardData(startDate, endDate)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Yenile"
                    >
                        <RefreshCw className={`w-5 h-5`} />
                        Yenile
                    </button>
                }
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePreviousPeriod}
                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                            if (date) {
                                const normalized = new Date(date);
                                normalized.setHours(0, 0, 0, 0);
                                setStartDate(normalized);
                            }
                        }}
                        maxDate={endDate}
                        dateFormat="dd/MM/yyyy"
                        locale={tr}
                        className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center min-w-[140px]"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                    <span className="text-gray-500">-</span>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                            if (date) {
                                const normalized = new Date(date);
                                normalized.setHours(23, 59, 59, 999);
                                setEndDate(normalized);
                            }
                        }}
                        minDate={startDate}
                        dateFormat="dd/MM/yyyy"
                        locale={tr}
                        className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center min-w-[140px]"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                    <button
                        onClick={handleNextPeriod}
                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex items-center gap-2 mt-2 lg:mt-0">
                    <button
                        onClick={() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            setStartDate(today);
                            const end = new Date(today);
                            end.setHours(23, 59, 59, 999);
                            setEndDate(end);
                        }}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Bugün
                    </button>
                    <button
                        onClick={() => {
                            const today = new Date();
                            const startOfWeek = new Date(today);
                            startOfWeek.setDate(today.getDate() - today.getDay());
                            startOfWeek.setHours(0, 0, 0, 0);
                            setStartDate(startOfWeek);

                            const endOfWeek = new Date(startOfWeek);
                            endOfWeek.setDate(startOfWeek.getDate() + 6);
                            endOfWeek.setHours(23, 59, 59, 999);
                            setEndDate(endOfWeek);
                        }}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Bu Hafta
                    </button>
                    <button
                        onClick={() => {
                            const today = new Date();
                            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                            startOfMonth.setHours(0, 0, 0, 0);
                            setStartDate(startOfMonth);

                            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                            endOfMonth.setHours(23, 59, 59, 999);
                            setEndDate(endOfMonth);
                        }}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Bu Ay
                    </button>
                    <button
                        onClick={() => {
                            const startOfYear = new Date(new Date().getFullYear(), 0, 1);
                            startOfYear.setHours(0, 0, 0, 0);
                            setStartDate(startOfYear);

                            const endOfYear = new Date(new Date().getFullYear(), 11, 31);
                            endOfYear.setHours(23, 59, 59, 999);
                            setEndDate(endOfYear);
                        }}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Tümü
                    </button>
                </div>
            </PageToolbar>

            <div className="px-4 py-4">
                {getDashboardContent()}

                <DashboardModals
                    showActiveCustomersModal={showActiveCustomersModal}
                    setShowActiveCustomersModal={setShowActiveCustomersModal}
                    showCashFlowModal={showCashFlowModal}
                    setShowCashFlowModal={setShowCashFlowModal}
                    dashboardData={dashboardData}
                    getDateRangeText={getDateRangeText}
                    showProfitModal={showProfitModal}
                    setShowProfitModal={setShowProfitModal}
                    showExpenseListModal={showExpenseListModal}
                    setShowExpenseListModal={setShowExpenseListModal}
                    showSoldQuantityModal={showSoldQuantityModal}
                    setShowSoldQuantityModal={setShowSoldQuantityModal}
                    soldProducts={soldProducts}
                    loadingSoldProducts={loadingSoldProducts}
                    showCarSoldQuantityModal={showCarSoldQuantityModal}
                    setShowCarSoldQuantityModal={setShowCarSoldQuantityModal}
                    carSoldProducts={carSoldProducts}
                    loadingCarSoldProducts={loadingCarSoldProducts}
                    cashExpenseDraft={cashExpenseDraft}
                    setCashExpenseDraft={setCashExpenseDraft}
                    cashExpenseRemoved={cashExpenseRemoved}
                    setCashExpenseRemoved={setCashExpenseRemoved}
                    cashExpenseDirty={cashExpenseDirty}
                    setCashExpenseDirty={setCashExpenseDirty}
                    savingCashExpenses={savingCashExpenses}
                    setSavingCashExpenses={setSavingCashExpenses}
                    cashExpenseModalLoading={cashExpenseModalLoading}
                    addCashExpenseDraft={addCashExpenseDraft}
                    removeCashExpenseDraft={removeCashExpenseDraft}
                    handleCashExpenseDraftChange={handleCashExpenseDraftChange}
                    handleSaveCashExpenses={handleSaveCashExpenses}
                    getTotalAmount={getTotalAmount}
                    showServiceDataModal={showServiceDataModal}
                    setShowServiceDataModal={setShowServiceDataModal}
                    serviceFormDraft={serviceFormDraft}
                    setServiceFormDraft={setServiceFormDraft}
                    serviceFormRemoved={serviceFormRemoved}
                    setServiceFormRemoved={setServiceFormRemoved}
                    serviceFormDirty={serviceFormDirty}
                    setServiceFormDirty={setServiceFormDirty}
                    savingServiceForm={savingServiceForm}
                    setSavingServiceForm={setSavingServiceForm}
                    addServiceDraftItem={addServiceDraftItem}
                    removeServiceDraftItem={removeServiceDraftItem}
                    handleServiceDraftChange={handleServiceDraftChange}
                    handleSaveServiceData={handleSaveServiceData}
                    showCashIncomeModal={showCashIncomeModal}
                    setShowCashIncomeModal={setShowCashIncomeModal}
                    cashIncomeDraft={cashIncomeDraft}
                    setCashIncomeDraft={setCashIncomeDraft}
                    cashIncomeRemoved={cashIncomeRemoved}
                    setCashIncomeRemoved={setCashIncomeRemoved}
                    cashIncomeDirty={cashIncomeDirty}
                    setCashIncomeDirty={setCashIncomeDirty}
                    savingCashIncome={savingCashIncome}
                    setSavingCashIncome={setSavingCashIncome}
                    cashIncomeModalLoading={cashIncomeModalLoading}
                    addCashIncomeDraft={addCashIncomeDraft}
                    removeCashIncomeDraft={removeCashIncomeDraft}
                    handleCashIncomeDraftChange={handleCashIncomeDraftChange}
                    handleSaveCashIncome={handleSaveCashIncome}
                />
            </div>
        </div>
    );
};

export default Dashboard;
